import React, { useState, useEffect } from 'react';
import { useContent } from '../../context/ContentContext';
import { resolveMediaUrl } from '../../utils/api';
import { activateCustomerDeviceNotifications } from '../../utils/oneSignal';
import { Download, CheckCircle2, Share, PlusSquare, MoreVertical, ArrowRight, Coffee, Bell, AlertCircle } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Capture beforeinstallprompt globally as early as possible so it is never missed before React mounts
let globalDeferredPrompt: BeforeInstallPromptEvent | null = null;
const promptListeners = new Set<(prompt: BeforeInstallPromptEvent | null) => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    globalDeferredPrompt = e as BeforeInstallPromptEvent;
    promptListeners.forEach((listener) => listener(globalDeferredPrompt));
  });

  window.addEventListener('appinstalled', () => {
    globalDeferredPrompt = null;
    promptListeners.forEach((listener) => listener(null));
  });
}

export function isPwaInstallUrl(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const searchParams = new URLSearchParams(window.location.search);
    let raw = searchParams.get('install');
    if (!raw && window.location.hash.includes('?')) {
      const hashQuery = window.location.hash.split('?')[1] || '';
      raw = new URLSearchParams(hashQuery).get('install');
    }
    return (raw || '').trim().toLowerCase() === 'pwa';
  } catch {
    return false;
  }
}

interface PwaInstallEntryProps {
  onContinueToWebsite: () => void;
  onDismiss?: () => void;
}

export const PwaInstallEntry: React.FC<PwaInstallEntryProps> = ({ onContinueToWebsite, onDismiss }) => {
  const { data } = useContent();
  const siteSettings = data?.siteSettings || { brandName: 'LETONKU', logoUrl: '' };

  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    () => globalDeferredPrompt
  );
  const [installState, setInstallState] = useState<'idle' | 'installing' | 'accepted' | 'dismissed'>('idle');
  const [logoError, setLogoError] = useState(false);
  const [isNotifActive, setIsNotifActive] = useState<boolean>(() => {
    return typeof Notification !== 'undefined' && Notification.permission === 'granted';
  });
  const [isActivatingNotif, setIsActivatingNotif] = useState<boolean>(false);
  const [notifMessage, setNotifMessage] = useState<string | null>(null);

  const handleActivatePush = async () => {
    if (isActivatingNotif) return;
    setIsActivatingNotif(true);
    setNotifMessage(null);
    try {
      const res = await activateCustomerDeviceNotifications();
      if (res.success) {
        setIsNotifActive(true);
        setNotifMessage('Notifikasi berhasil diaktifkan!');
      } else {
        setNotifMessage(res.error || 'Izin belum diizinkan.');
      }
    } catch {
      setNotifMessage('Gagal mengaktifkan notifikasi.');
    } finally {
      setIsActivatingNotif(false);
    }
  };

  const [deviceInfo] = useState(() => {
    if (typeof window === 'undefined') {
      return { isIOS: false, isAndroid: false, isStandalone: false };
    }
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS =
      /iphone|ipad|ipod/.test(ua) ||
      (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
    const isAndroid = /android/.test(ua);
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true;

    return { isIOS, isAndroid, isStandalone };
  });

  useEffect(() => {
    const handlePromptUpdate = (prompt: BeforeInstallPromptEvent | null) => {
      setDeferredPrompt(prompt);
    };
    promptListeners.add(handlePromptUpdate);

    const onAppInstalled = () => {
      setInstallState('accepted');
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      promptListeners.delete(handlePromptUpdate);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    try {
      setInstallState('installing');
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      if (choiceResult.outcome === 'accepted') {
        globalDeferredPrompt = null;
        setDeferredPrompt(null);
        setInstallState('accepted');
      } else {
        setInstallState('dismissed');
      }
    } catch {
      setInstallState('dismissed');
    }
  };

  const resolvedLogo = siteSettings.logoUrl
    ? resolveMediaUrl(siteSettings.logoUrl)
    : '/icon-192.png';

  const isInstalled = installState === 'accepted' || deviceInfo.isStandalone;
  const hasNativePrompt = !!deferredPrompt;

  return (
    <div className="fixed inset-0 z-[100] min-h-screen w-full bg-[#F8FBFF] text-[#172033] flex flex-col items-center justify-center p-5 sm:p-6 overflow-y-auto">
      {/* Subtle Ambient Background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[480px] h-[480px] rounded-full bg-[#E0F2FE]/70 blur-3xl" />
      </div>

      <main className="relative z-10 w-full max-w-md mx-auto">
        <div className="bg-white rounded-3xl border border-[#E0F2FE] shadow-[0_20px_60px_rgba(15,23,42,0.06)] p-7 sm:p-9 text-center">
          {/* LetonKu Logo */}
          <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 rounded-2xl overflow-hidden border border-[#E0F2FE] shadow-sm bg-[#F0F7FF] flex items-center justify-center">
            {!logoError ? (
              <img
                src={resolvedLogo}
                alt={siteSettings.brandName || 'LetonKu'}
                className="w-full h-full object-cover"
                onError={() => setLogoError(true)}
              />
            ) : (
              <Coffee className="w-10 h-10 text-[#0284C7]" />
            )}
          </div>

          {/* Brand Badge */}
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0284C7] mb-2">
            {siteSettings.brandName || 'LETONKU'}
          </p>

          {/* Headline */}
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-[#172033] mb-3">
            Install LetonKu
          </h1>

          {/* Short Explanation */}
          <p className="text-sm text-[#64748B] leading-relaxed mb-7">
            Akses menu lebih cepat, pesan kopi tanpa antre, dan terima update status pesanan langsung dari layar utama perangkat Anda.
          </p>

          {/* CASE 1: Install Accepted / Already Installed */}
          {isInstalled ? (
            <div className="space-y-4 mb-6">
              <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-2xl p-5 text-center">
                <CheckCircle2 className="w-10 h-10 text-[#16A34A] mx-auto mb-2.5" />
                <h2 className="font-display font-bold text-base text-[#14532D] mb-1">
                  LetonKu Siap Digunakan
                </h2>
                <p className="text-xs text-[#15803D] leading-relaxed">
                  Aplikasi berhasil ditambahkan. Anda kini dapat membuka LetonKu langsung dari layar utama perangkat Anda.
                </p>
              </div>

              {!isNotifActive ? (
                <div className="bg-[#F0F7FF] border border-[#E0F2FE] rounded-2xl p-4 text-center">
                  <Bell className="w-6 h-6 text-[#0284C7] mx-auto mb-2" />
                  <p className="font-bold text-xs text-[#172033] mb-1">
                    Aktifkan Notifikasi Pesanan
                  </p>
                  <p className="text-[11px] text-[#64748B] mb-3">
                    Dapatkan update status pesanan otomatis di perangkat ini tanpa antre.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleActivatePush}
                      disabled={isActivatingNotif}
                      className="flex-1 py-2.5 px-4 rounded-xl bg-[#0284C7] hover:bg-[#0369A1] text-white text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer disabled:opacity-50"
                    >
                      <Bell className="w-4 h-4" />
                      <span>{isActivatingNotif ? 'Memproses...' : 'Aktifkan Notifikasi'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={onDismiss || onContinueToWebsite}
                      className="py-2.5 px-3 rounded-xl border border-[#E0F2FE] hover:bg-white text-[#64748B] text-xs font-semibold transition-colors cursor-pointer"
                    >
                      Nanti saja
                    </button>
                  </div>
                  {notifMessage && (
                    <p className="text-[11px] text-[#0284C7] mt-2 font-medium">
                      {notifMessage}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center flex items-center justify-center gap-2 text-xs font-bold text-emerald-800">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Notifikasi Pesanan Aktif</span>
                </div>
              )}
            </div>
          ) : hasNativePrompt ? (
            /* CASE 2: Native Install Prompt Available (Android Chrome / Desktop Chromium) */
            <div className="mb-6">
              <button
                type="button"
                onClick={handleInstallClick}
                disabled={installState === 'installing'}
                className="w-full py-4 px-6 rounded-2xl bg-[#0284C7] hover:bg-[#0369A1] active:scale-[0.99] text-white font-display font-extrabold text-sm sm:text-base tracking-wide uppercase shadow-lg shadow-[#0284C7]/25 transition-all flex items-center justify-center gap-2.5 cursor-pointer disabled:opacity-60"
              >
                <Download className="w-5 h-5 shrink-0" />
                <span>
                  {installState === 'installing' ? 'MEMPROSES...' : 'INSTALL LETONKU'}
                </span>
              </button>

              {installState === 'dismissed' && (
                <p className="text-xs text-[#64748B] mt-3">
                  Instalasi dibatalkan. Anda dapat menekan tombol di atas kapan saja untuk menginstal.
                </p>
              )}
            </div>
          ) : deviceInfo.isIOS ? (
            /* CASE 3: iPhone / iPad Safari (No native beforeinstallprompt) */
            <div className="bg-[#F8FBFF] border border-[#E0F2FE] rounded-2xl p-5 mb-6 text-left">
              <p className="text-xs font-bold uppercase tracking-wider text-[#0284C7] mb-3.5 text-center">
                Cara Install di iPhone / Safari
              </p>
              <ol className="space-y-3 text-sm text-[#172033]">
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#E0F2FE] text-[#0284C7] font-display font-bold text-xs flex items-center justify-center shrink-0">
                    1
                  </span>
                  <span className="flex-1">
                    Tekan tombol <strong>Share</strong>{' '}
                    <Share className="inline w-4 h-4 text-[#0284C7] -mt-0.5 mx-0.5" /> di bar Safari
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#E0F2FE] text-[#0284C7] font-display font-bold text-xs flex items-center justify-center shrink-0">
                    2
                  </span>
                  <span className="flex-1">
                    Pilih <strong>&quot;Add to Home Screen&quot;</strong>{' '}
                    <PlusSquare className="inline w-4 h-4 text-[#0284C7] -mt-0.5 mx-0.5" />
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#E0F2FE] text-[#0284C7] font-display font-bold text-xs flex items-center justify-center shrink-0">
                    3
                  </span>
                  <span className="flex-1">
                    Tekan <strong>&quot;Add&quot;</strong> di pojok kanan atas
                  </span>
                </li>
              </ol>
            </div>
          ) : (
            /* CASE 4: Desktop or Android Browser when native prompt is not currently exposed */
            <div className="bg-[#F8FBFF] border border-[#E0F2FE] rounded-2xl p-5 mb-6 text-center">
              {deviceInfo.isAndroid ? (
                <p className="text-xs sm:text-sm text-[#475569] leading-relaxed">
                  Untuk menambahkan aplikasi, tekan menu browser{' '}
                  <MoreVertical className="inline w-4 h-4 text-[#0284C7] -mt-0.5" /> lalu pilih{' '}
                  <strong className="text-[#172033]">&quot;Install app&quot;</strong> atau{' '}
                  <strong className="text-[#172033]">&quot;Tambahkan ke Layar utama&quot;</strong>.
                </p>
              ) : (
                <p className="text-xs sm:text-sm text-[#475569] leading-relaxed">
                  LetonKu dapat ditambahkan ke Home Screen / Desktop melalui menu browser Anda.
                </p>
              )}
            </div>
          )}

          {/* Secondary Action: Continue to Normal Website */}
          <button
            type="button"
            onClick={onContinueToWebsite}
            className="inline-flex items-center justify-center gap-1.5 text-xs font-bold text-[#64748B] hover:text-[#0284C7] transition-colors py-2 px-3 cursor-pointer"
          >
            <span>Lanjut ke Website LetonKu</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </main>
    </div>
  );
};
